"use client";

import { useState, useRef } from "react";
import { X } from "lucide-react";

interface VideoPlaceholderProps {
  onClose?: () => void;
}

export function VideoPlaceholder({ onClose }: VideoPlaceholderProps) {
  const [width, setWidth] = useState(320); // Default width in pixels
  const [height, setHeight] = useState(200); // Default height in pixels
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const startWidthRef = useRef(0);
  const startHeightRef = useRef(0);

  const MIN_WIDTH = 200;
  const MAX_WIDTH = 600;
  const MIN_HEIGHT = 100;
  const MAX_HEIGHT = 600;

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    startXRef.current = e.clientX;
    startYRef.current = e.clientY;
    startWidthRef.current = width;
    startHeightRef.current = height;

    // Add event listeners to document for smoother dragging
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing && e.buttons !== 1) return;

      const deltaX = e.clientX - startXRef.current;
      const deltaY = e.clientY - startYRef.current;

      const newWidth = Math.max(
        MIN_WIDTH,
        Math.min(MAX_WIDTH, startWidthRef.current - deltaX)
      );
      const newHeight = Math.max(
        MIN_HEIGHT,
        Math.min(MAX_HEIGHT, startHeightRef.current - deltaY)
      );

      setWidth(newWidth);
      setHeight(newHeight);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  return (
    <div
      ref={containerRef}
      className="relative border-2 border-muted rounded-lg bg-muted/10 flex items-center justify-center transition-colors"
      style={{ width: `${width}px`, height: `${height}px` }}
    >
      {/* Close button (top-right corner) */}
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-0 right-0 w-6 h-6 rounded-full text-muted-foreground hover:text-foreground flex items-center justify-center shadow-md transition-colors z-20"
          title="Close video placeholder"
        >
          <X className="w-4 h-4" />
        </button>
      )}

      {/* Resize handle (top-left corner) */}
      <div
        onMouseDown={handleMouseDown}
        className={`absolute -top-1 -left-1 w-8 h-8 cursor-nwse-resize group ${
          isResizing ? "z-50" : "z-10"
        }`}
        title="Drag to resize"
      >
        {/* Visual indicator */}
        <div className="absolute top-1 left-1 w-6 h-6 flex items-start justify-start">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            className="text-muted-foreground group-hover:text-foreground transition-colors"
          >
            {/* Corner lines */}
            <path
              d="M4 4L10 10M4 4L4 10M4 4L10 4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </div>
      </div>

      {/* Placeholder content */}
      <div className="text-center text-muted-foreground">
        <div className="text-sm font-medium">Video Feed Placeholder</div>
        <div className="text-xs mt-1">
          {width}px × {height}px
        </div>
      </div>

      {/* Resizing overlay */}
      {isResizing && <div className="fixed inset-0 z-40 cursor-nwse-resize" />}
    </div>
  );
}
